import { useState } from "react";
import { Send, Loader2, Paperclip, X, Mail } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useSite, tx } from "@/lib/lang";
import { ui } from "@/lib/uiStrings";
import { Reveal, SectionLabel } from "./Reveal";

const EMPTY = { nome: "", azienda: "", email: "", ruolo: "", telefono: "", consumo_gwh: "", tipologia_cliente: "", scadenza_contratto: "", messaggio: "" };

const inputCls =
  "w-full bg-onyx/60 border border-ice/10 rounded-lg px-4 py-3 text-sm text-ice placeholder:text-muted-foreground/50 outline-none focus:border-royal transition-colors";

export default function Contact() {
  const { content, lang } = useSite();
  const t0 = ui[lang].contact;
  const cc = content.contatti || {};
  const sz = content.sezioni?.contatti || {};
  const t = { ...t0 };
  ["nome", "azienda", "email", "ruolo", "telefono", "consumo", "tipologia", "scadenza", "bolletta", "messaggio", "submit", "sending", "success", "error", "privacy", "scegli_file"].forEach((k) => {
    const v = tx(cc[k], lang);
    if (v) t[k] = v;
  });
  t.consumoOptions = cc.consumoOptions?.[lang] || cc.consumoOptions?.it || t0.consumoOptions;
  t.tipologiaOptions = cc.tipologiaOptions?.[lang] || cc.tipologiaOptions?.it || t0.tipologiaOptions;
  t.heading = tx(sz.heading, lang) || t0.heading;
  t.sub = tx(sz.sub, lang) || t0.sub;
  t.quote = tx(sz.quote, lang) || (lang === "it" ? "“Non vendiamo solo un contratto luce. Costruiamo una fornitura raccontabile.”" : "“We don't just sell a power contract. We build a supply you can talk about.”");
  const [form, setForm] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => v && fd.append(k, v));
      fd.append("lang", lang);
      if (file) fd.append("bolletta", file);
      await api.post("/contact", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t.success);
      setForm(EMPTY);
      setFile(null);
    } catch (err) {
      toast.error(formatApiError(err) || t.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contatti" data-testid="contact-section" className="py-16 lg:py-24">
      <div className="mx-auto max-w-7xl px-6 grid lg:grid-cols-[0.9fr_1.1fr] gap-16 items-start">
        <Reveal className="lg:sticky lg:top-28">
          <SectionLabel>{ui[lang].labels.contatti}</SectionLabel>
          <h2 className="mt-5 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-snug text-ice">
            {t.heading}
          </h2>
          <p className="mt-4 text-base text-muted-foreground leading-relaxed max-w-md">{t.sub}</p>
          <div className="mt-10 border-l-2 border-royal pl-5">
            <p className="font-serif italic text-lg text-azure leading-relaxed">
              {t.quote}
            </p>
          </div>
          {content.contatti?.owner_email && (
            <a
              data-testid="contact-email-link"
              href={`mailto:${content.contatti.owner_email}`}
              className="mt-8 inline-flex items-center gap-3 text-sm font-medium text-ice hover:text-royal transition-colors group"
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-royal/10 border border-royal/20 text-royal group-hover:bg-royal group-hover:text-white transition-colors">
                <Mail size={15} />
              </span>
              {content.contatti.owner_email}
            </a>
          )}
        </Reveal>

        <Reveal delay={0.15}>
          <form onSubmit={submit} className="bg-surface border border-ice/8 rounded-2xl p-6 sm:p-10 grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className="block text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground mb-2">{t.nome} *</label>
              <input data-testid="contact-form-name" required value={form.nome} onChange={set("nome")} className={inputCls} placeholder={lang === "it" ? "Mario Rossi" : "John Smith"} />
            </div>
            <div>
              <label className="block text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground mb-2">{t.azienda} *</label>
              <input data-testid="contact-form-company" required value={form.azienda} onChange={set("azienda")} className={inputCls} placeholder="Acme SpA" />
            </div>
            <div>
              <label className="block text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground mb-2">{t.email} *</label>
              <input data-testid="contact-form-email" required type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="mario@acme.it" />
            </div>
            <div>
              <label className="block text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground mb-2">{t.ruolo}</label>
              <input data-testid="contact-form-role" value={form.ruolo} onChange={set("ruolo")} className={inputCls} placeholder="CFO / Energy Manager" />
            </div>
            <div>
              <label className="block text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground mb-2">{t.telefono}</label>
              <input data-testid="contact-form-phone" value={form.telefono} onChange={set("telefono")} className={inputCls} placeholder="+39 ..." />
            </div>
            <div>
              <label className="block text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground mb-2">{t.consumo}</label>
              <select data-testid="contact-form-gwh-select" value={form.consumo_gwh} onChange={set("consumo_gwh")} className={inputCls}>
                <option value="">—</option>
                {t.consumoOptions.map((o) => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground mb-2">{t.tipologia}</label>
              <select data-testid="contact-form-client-type" value={form.tipologia_cliente} onChange={set("tipologia_cliente")} className={inputCls}>
                <option value="">—</option>
                {t.tipologiaOptions.map((o) => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground mb-2">{t.scadenza}</label>
              <input data-testid="contact-form-contract-expiry" type="month" value={form.scadenza_contratto} onChange={set("scadenza_contratto")} className={inputCls} />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground mb-2">{t.bolletta}</label>
              <div className="flex items-center gap-3">
                <label
                  data-testid="contact-form-bill-upload"
                  className="inline-flex items-center gap-2 border border-dashed border-royal/50 hover:border-azure text-azure text-sm font-medium px-4 py-2.5 rounded-lg cursor-pointer transition-colors"
                >
                  <Paperclip size={15} />
                  {t.scegli_file}
                  <input
                    type="file"
                    accept="application/pdf"
                    className="hidden"
                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                  />
                </label>
                {file && (
                  <span className="inline-flex items-center gap-2 text-xs text-ice/80 bg-onyx/60 border border-ice/10 rounded-lg px-3 py-2">
                    {file.name}
                    <button type="button" onClick={() => setFile(null)} className="text-muted-foreground hover:text-red-400">
                      <X size={13} />
                    </button>
                  </span>
                )}
              </div>
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground mb-2">{t.messaggio}</label>
              <textarea data-testid="contact-form-message" rows={4} value={form.messaggio} onChange={set("messaggio")} className={inputCls} placeholder={lang === "it" ? "Raccontaci il tuo contesto…" : "Tell us about your context…"} />
            </div>
            <div className="sm:col-span-2 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <p className="text-xs text-muted-foreground max-w-xs">{t.privacy}</p>
              <button
                data-testid="contact-form-submit-button"
                type="submit"
                disabled={sending}
                className="inline-flex items-center gap-2 bg-royal hover:bg-azure hover:text-[#0B1424] transition-colors text-white font-semibold px-6 py-3 rounded-full disabled:opacity-60"
              >
                {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                {sending ? t.sending : t.submit}
              </button>
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
